import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { faPlugCircleCheck } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  updateOpenModalCreateNewAdapter,
  updateAdapter,
} from "../../../redux/adapters/adapterSlice";
import { usePostAdapterMutation } from "../../../redux/adapters/adapterAPI";
// CUSTOM HOOKS
import useGetOneAdapter from "../../../customHooks/GET_requests/useGetOneAdapter";
import usePutAdapter from "../../../customHooks/PUT_requests/usePutAdapter";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// COMPONENTS
import FormInputBooleanSwitch from "../../components/form/FormInputBooleanSwitch";
import FormInputText from "../../components/form/FormInputText";
import FormSelectLiftType from "../../components/form/FormSelectLiftType";
import FormSelectAdapterType from "../../components/form/FormSelectAdapterType";
import FormSelectAuthType from "../../components/form/FormSelectAuthType";
import FormInputIPandPort from "../../components/form/FormInputIPandPort";
import FormInputNumber from "../../components/form/FormInputNumber";
import FormSelectProtocolType from "../../components/form/FormSelectProtocolType";
import FormSelectProductType from "../../components/form/FormSelectProductType";
import FormSelectLeadingZeroes from "../../components/form/FormSelectLeadingZeroes";
import GlobalAlert2 from "../../home/GlobalAlert2";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import ButtonClose from "../../components/buttons/ButtonClose";
import ButtonSave from "../../components/buttons/ButtonSave";
import FormSelectProtocolTypeEmun from "../../components/form/FormSelectProtocolTypeEmun";
import CdoOpcPage from "./CDO/OPC/CdoOpcPage";
import CdoSequentPage from "./CDO/SEQUENT/CdoSequentPage";
import CdoModbusPage from "./CDO/MODBUS/CdoModbusPage";
// UTILS
import { extractCdoState } from "./extractCdoState";
// INTERFACES
import { RootState } from "../../../app/store";
import {
  AdapterInterface,
  AdapterCdoMceInterface,
  AdapterCdoLn1Interface,
} from "../../../interfaces/Adapter.interface";
import { AdapterCdoModbusInterface } from "../../../interfaces/AdapterCdoModbus.interface";
import { AdapterCdoOpcInterface } from "../../../interfaces/AdapterCdoOpc.interface";
import { AdapterCdoSequentInterface } from "../../../interfaces/AdapterCdoSequent.interface";
import { LiftType } from "../../../interfaces/Lift.interface";
// CONSTANTS
import {
  BLANK_ADAPTER,
  BLANK_ADAPTER_CDO_MCE,
  BLANK_ADAPTER_CDO_MODBUS,
  BLANK_ADAPTER_CDO_LN1,
  BLANK_ADAPTER_CDO_OPC,
  BLANK_ADAPTER_CDO_SEQUENT,
} from "../../../constants/adapter";

function AdaptersCreateEditForm() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [adapter, setAdapter] = useState<AdapterInterface>({
    ...BLANK_ADAPTER,
  });
  const [cdoMce, setCdoMce] = useState<AdapterCdoMceInterface>({
    ...BLANK_ADAPTER_CDO_MCE,
  });
  const [cdoModbus, setCdoModbus] = useState<AdapterCdoModbusInterface>({
    ...BLANK_ADAPTER_CDO_MODBUS,
  });
  const [cdoSequent, setCdoSequent] = useState<AdapterCdoSequentInterface>({
    ...BLANK_ADAPTER_CDO_SEQUENT,
  });
  const [cdoOpc, setCdoOpc] = useState<AdapterCdoOpcInterface>({
    ...BLANK_ADAPTER_CDO_OPC,
  });
  const [cdoLn1, setCdoLn1] = useState<AdapterCdoLn1Interface>({
    ...BLANK_ADAPTER_CDO_LN1,
  });
  const [jsonStringDictionary, setJsonStringDictionary] = useState<string>("");
  const [jsonStringInterface, setJsonStringInterface] = useState<string>("");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    openModalCreateNewAdapter,
    formCreateEdit,
    adapter: selectedAdapter,
  } = useSelector((state: RootState) => state.adapter);
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerPostAdapter,
    {
      isLoading: isLoadingPostAdapter,
      isSuccess: isSuccessPostAdapter,
      isError: isErrorPostAdapter,
      error: errorPostAdapter,
      reset: resetPostAdapter,
    },
  ] = usePostAdapterMutation();

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { adapter: adapterHook, isLoadingGetOneAdapter } = useGetOneAdapter({
    id: selectedAdapter.id ?? "",
  });
  const { putAdapter, isLoadingPutAdapter, isSuccessPutAdapter, isErrorPutAdapter } =
    usePutAdapter();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickClose = () => {
    resetPostAdapter();
    setAdapter({ ...BLANK_ADAPTER });
    setCdoMce({ ...BLANK_ADAPTER_CDO_MCE });
    setCdoModbus({ ...BLANK_ADAPTER_CDO_MODBUS });
    setCdoOpc({ ...BLANK_ADAPTER_CDO_OPC });
    setCdoLn1({ ...BLANK_ADAPTER_CDO_LN1 });
    setCdoSequent({ ...BLANK_ADAPTER_CDO_SEQUENT });
    setJsonStringDictionary("");
    setJsonStringInterface("");
    dispatch(updateOpenModalCreateNewAdapter(false));
    dispatch(updateAdapter({ ...BLANK_ADAPTER }));
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();
    const copy: AdapterInterface = { ...adapter };
    if (copy.protocol_type.toLowerCase() === "mce") {
      copy.cdo_input = cdoMce;
    } else if (copy.protocol_type.toLowerCase() === "modbus") {
      copy.cdo_input = cdoModbus;
    } else if (copy.protocol_type.toLowerCase() === "opc") {
      copy.cdo_input = cdoOpc;
    } else if (copy.protocol_type.toLowerCase() === "sequent") {
      copy.cdo_input = cdoSequent;
    } else if (["ln1", "cga"].includes(copy.protocol_type.toLowerCase())) {
      copy.cdo_input = cdoLn1;
    } else {
      copy.cdo_input = {};
    }
    if (formCreateEdit === "CREATE" || formCreateEdit === "DUPLICATE") {
      triggerPostAdapter({ token, body: copy });
    } else {
      if (!copy.offset_mapping) {
        copy.offset_mapping = [];
      }
      putAdapter(copy);
    }
  };

  const onChangeFormValueCdoMce = (newValue: string | number, variableName: string) => {
    const copy: AdapterCdoMceInterface = structuredClone(cdoMce);
    if (variableName === "idl_file" && typeof newValue === "string") {
      copy.idl_file = newValue;
    } else if (variableName === "reporting_address" && typeof newValue === "string") {
      copy.event_reporting_address = newValue;
    } else if (variableName === "mapping_data" && typeof newValue === "string") {
      copy.event_mapping_data = newValue;
    } else if (variableName === "mapping_file" && typeof newValue === "string") {
      copy.event_mapping_file = newValue;
    }
    setCdoMce(copy);
  };

  const onChangeFormValueCdoLn1 = (newValue: boolean | number, variableName: string) => {
    const copy: AdapterCdoLn1Interface = structuredClone(cdoLn1);
    if (variableName === "crc_override" && typeof newValue === "boolean") {
      copy.crc_override = newValue;
    } else if (variableName === "should_filter_duplicates" && typeof newValue === "boolean") {
      copy.should_filter_duplicates = newValue;
    } else if (variableName === "allowed_messages" && typeof newValue === "number") {
      copy.allowed_messages = newValue;
    } else if (variableName === "from_device_serial" && typeof newValue === "number") {
      copy.terminating_zeroes_expected.from_device_serial = newValue;
    } else if (variableName === "refresh_all" && typeof newValue === "number") {
      copy.terminating_zeroes_expected.refresh_all = newValue;
    } else if (variableName === "set_one_bit" && typeof newValue === "number") {
      copy.terminating_zeroes_expected.set_one_bit = newValue;
    } else if (variableName === "reinitialize_all_devices" && typeof newValue === "number") {
      copy.terminating_zeroes_expected.reinitialize_all_devices = newValue;
    } else if (variableName === "set_one_byte" && typeof newValue === "number") {
      copy.terminating_zeroes_expected.set_one_byte = newValue;
    } else if (variableName === "rvu" && typeof newValue === "number") {
      copy.terminating_zeroes_expected.rvu = newValue;
    } else if (variableName === "rvu_heartbeat" && typeof newValue === "number") {
      copy.terminating_zeroes_expected.rvu_heartbeat = newValue;
    }
    setCdoLn1(copy);
  };

  const onChangeFormValueLiftType = (newValue: LiftType) => {
    setAdapter((prev: AdapterInterface) => ({ ...prev, lift_type: newValue }));
  };

  const onChangeFormValue = (newValue: string | number, variableName: string) => {
    const copy: AdapterInterface = structuredClone(adapter);
    if (variableName === "name" && typeof newValue === "string") {
      copy.name = newValue;
    } else if (variableName === "type" && typeof newValue === "string") {
      copy.type = newValue;
    } else if (variableName === "version" && typeof newValue === "string") {
      copy.version = newValue;
    } else if (variableName === "socket_address" && typeof newValue === "string") {
      copy.socket_address = newValue;
    } else if (variableName === "auth_type" && typeof newValue === "string") {
      copy.auth_type = newValue;
    } else if (variableName === "auth_type" && typeof newValue === "string") {
      copy.auth_type = newValue;
    } else if (variableName === "units" && typeof newValue === "number") {
      copy.units = newValue;
    } else if (variableName === "floors" && typeof newValue === "number") {
      copy.floors = newValue;
    }
    setAdapter(copy);
  };

  const onChangeFormValueAuthCredentials = (newValue: string, variableName: string) => {
    const copy: AdapterInterface = structuredClone(adapter);
    copy.auth_credentials[variableName] = newValue;
    setAdapter(copy);
  };

  const onChangeProtocolValue = (
    protocolId: string,
    protocolName: string,
    protocolDefaultInterface: string,
  ) => {
    const copy: AdapterInterface = structuredClone(adapter);
    copy.protocol_id = protocolId;
    copy.interface = JSON.parse(protocolDefaultInterface);
    setAdapter(copy);
  };

  const onChangeProtocolType = (newValue: string) => {
    const copy: AdapterInterface = structuredClone(adapter);
    copy.protocol_type = newValue;
    setAdapter(copy);
  };

  const onChangeProductTypeValue = (newValue: number) => {
    const copy: AdapterCdoMceInterface = structuredClone(cdoMce);
    copy.product_type = newValue;
    setCdoMce(copy);
  };

  const onChangeLeadingZeroesValue = (id: string) => {
    const copy: AdapterInterface = structuredClone(adapter);
    copy.leading_zeroes_id = id;
    setAdapter(copy);
  };

  const disableSaveButton = () => {
    if (
      adapter.name === "" ||
      adapter.version === "" ||
      adapter.socket_address === "" ||
      adapter.protocol_type === "" ||
      (adapter.auth_credentials.password === "" && formCreateEdit === "CREATE") ||
      (adapter.auth_credentials.username === "" && formCreateEdit === "CREATE") ||
      (adapter.auth_credentials.password === "" && formCreateEdit === "DUPLICATE") ||
      (adapter.auth_credentials.username === "" && formCreateEdit === "DUPLICATE")
    ) {
      return true;
    }
    return false;
  };

  const isValidJSON = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const getTitle = () => {
    if (formCreateEdit === "CREATE") {
      return "adapter.createNewAdapter";
    }
    if (formCreateEdit === "EDIT") {
      return "adapter.editAdapter";
    }
    return "general.duplicateAdapter";
  };
  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    onClickClose();
  }, [isSuccessPostAdapter, isSuccessPutAdapter, isErrorPostAdapter, isErrorPutAdapter]);

  useEffect(() => {
    if (isValidJSON(jsonStringDictionary)) {
      const copy: AdapterInterface = structuredClone(adapter);
      copy.dictionary = JSON.parse(jsonStringDictionary);
      setAdapter(copy);
    }
  }, [jsonStringDictionary]);

  useEffect(() => {
    if (isValidJSON(jsonStringInterface)) {
      const copy: AdapterInterface = structuredClone(adapter);
      copy.interface = JSON.parse(jsonStringInterface);
      setAdapter(copy);
    }
  }, [jsonStringInterface]);

  useEffect(() => {
    if (adapterHook && adapterHook.id) {
      if ((formCreateEdit === "EDIT" || formCreateEdit === "DUPLICATE") && adapterHook) {
        setAdapter(adapterHook);
        const cdo = extractCdoState(adapterHook);
        if (cdo.cdoMce) setCdoMce(cdo.cdoMce);
        if (cdo.cdoLn1) setCdoLn1(cdo.cdoLn1);
        if (cdo.cdoSequent) setCdoSequent(cdo.cdoSequent);
        if (cdo.cdoOpc) setCdoOpc(cdo.cdoOpc);
        if (cdo.modbus) setCdoModbus(cdo.modbus);
      }

      setJsonStringDictionary(JSON.stringify(adapterHook.dictionary));
      setJsonStringInterface(JSON.stringify(adapterHook.interface));
    }
  }, [adapterHook]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Drawer
      width="800"
      placement="right"
      onClose={onClickClose}
      closable={false}
      open={openModalCreateNewAdapter}
      footer={
        <div className="container generalStyles__flex mt-4">
          <div className="generalStyles__width100 generalStyles__mrFix">
            <ButtonClose onClick={onClickClose} />
          </div>
          <div className="generalStyles__width100 generalStyles__mlFix">
            <ButtonSave
              onClick={onClickSave}
              disabledButton={disableSaveButton()}
              isLoading={false}
            />
          </div>
        </div>
      }
    >
      <Spin spinning={isLoadingPostAdapter || isLoadingPutAdapter || isLoadingGetOneAdapter}>
        {/** ************************************************* */}
        {/** COMPONENT DRAWER DIVIDER */}
        <DrawerRowDivider icon={faPlugCircleCheck} title={t(getTitle())} />
        {/** ------------------------------------------------------------- */}
        {/** ADAPTER FORM  */}
        <div className="container">
          <div className={`drawer__box__${theme} row`}>
            {/** COMPONENTS FORM - PROTOCOL */}
            <FormSelectProtocolType
              inputValue={adapter.protocol_id}
              inputOnChange={onChangeProtocolValue}
              inputLabelName={t("general.protocol")}
            />
            {/** COMPONENTS FORM - PROTOCOL  TYPE NAME ENUM */}
            <FormSelectProtocolTypeEmun
              value={adapter.protocol_type}
              onChangeProtocolTypeEnum={onChangeProtocolType}
              disabledInput={false}
            />
            {/** COMPONENTS FORM - INPUT NAME */}
            <FormInputText
              inputValue={adapter.name}
              inputLabelName={t("general.name")}
              inputOnChange={onChangeFormValue}
              inputVariableName="name"
              isRequired
              disabledInput={false}
            />
            {/** COMPONENTS FORM - SELECT LIFT TYPE */}
            <FormSelectLiftType
              inputValue={adapter.lift_type}
              inputOnChange={onChangeFormValueLiftType}
            />
            {/** COMPONENTS FORM - SELECT ADAPTER TYPE */}
            <FormSelectAdapterType
              inputValue={adapter.type}
              inputOnChange={onChangeFormValue}
              inputLabelName="Adapter Type"
              inputVariableName="type"
            />
            {/** COMPONENTS FORM - INPUT VERSION */}
            <FormInputText
              inputValue={adapter.version}
              inputLabelName={t("adapter.version")}
              inputOnChange={onChangeFormValue}
              inputVariableName="version"
              isRequired
              disabledInput={false}
            />
            {/** COMPONENTS FORM - INPUT SOCKET ADDRESS */}
            <FormInputIPandPort
              inputValue={adapter.socket_address}
              inputLabelName={t("edgeManagement.channels.socketAddress")}
              inputOnChange={onChangeFormValue}
              inputVariableName="socket_address"
            />
            {/** COMPONENTS FORM - UNITS */}
            <FormInputNumber
              inputValue={adapter.units}
              inputOnChange={onChangeFormValue}
              inputLabelName="Units"
              inputVariableName="units"
            />
            {/** COMPONENTS FORM - FLOORS */}
            <FormInputNumber
              inputValue={adapter.floors}
              inputOnChange={onChangeFormValue}
              inputLabelName="Floors"
              inputVariableName="floors"
            />
            {/** COMPONENTS FORM - LEADING ZEROES */}
            <FormSelectLeadingZeroes
              inputValue={adapter.leading_zeroes_id}
              inputOnChange={onChangeLeadingZeroesValue}
              inputLabelName={t("general.leadingZeroes")}
            />
          </div>
        </div>
        <br />
        {["ln1", "mce", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
          <>
            {/** ************************************************* */}
            {/** COMPONENT DRAWER DIVIDER */}
            <DrawerRowDivider
              icon={faPlugCircleCheck}
              title={adapter.protocol_type.toUpperCase()}
            />
            {/** ------------------------------------------------------------- */}
            {/** ADAPTER FORM  */}
            <div className="container">
              <div className={`drawer__box__${theme} row`}>
                {/** COMPONENTS FORM - CRC OVERRIDE */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputBooleanSwitch
                    value={cdoLn1.crc_override}
                    onChangeSwitch={onChangeFormValueCdoLn1}
                    labelName={t("general.crcOverride")}
                    variableName="crc_override"
                    isRequired
                    disabledInput={false}
                  />
                )}
                {/** COMPONENTS FORM - SHOULD FILTER DUPLICATES */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputBooleanSwitch
                    value={cdoLn1.should_filter_duplicates}
                    onChangeSwitch={onChangeFormValueCdoLn1}
                    labelName={t("general.shouldFilterDuplicates")}
                    variableName="should_filter_duplicates"
                    isRequired
                    disabledInput={false}
                  />
                )}
                {/** COMPONENTS FORM - ALLOWED MESSAGES */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputNumber
                    inputValue={cdoLn1.allowed_messages}
                    inputOnChange={onChangeFormValueCdoLn1}
                    inputLabelName={t("general.allowedMessages")}
                    inputVariableName="allowed_messages"
                  />
                )}
                {/** COMPONENTS FORM - */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputNumber
                    inputValue={cdoLn1.terminating_zeroes_expected.from_device_serial}
                    inputOnChange={onChangeFormValueCdoLn1}
                    inputLabelName={t("general.fromDeviceSerial")}
                    inputVariableName="from_device_serial"
                  />
                )}
                {/** COMPONENTS FORM - */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputNumber
                    inputValue={cdoLn1.terminating_zeroes_expected.refresh_all}
                    inputOnChange={onChangeFormValueCdoLn1}
                    inputLabelName={t("general.refreshAll")}
                    inputVariableName="refresh_all"
                  />
                )}
                {/** COMPONENTS FORM - */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputNumber
                    inputValue={cdoLn1.terminating_zeroes_expected.set_one_bit}
                    inputOnChange={onChangeFormValueCdoLn1}
                    inputLabelName={t("general.setOneBit")}
                    inputVariableName="set_one_bit"
                  />
                )}
                {/** COMPONENTS FORM - */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputNumber
                    inputValue={cdoLn1.terminating_zeroes_expected.reinitialize_all_devices}
                    inputOnChange={onChangeFormValueCdoLn1}
                    inputLabelName={t("general.reinitializeAllDevices")}
                    inputVariableName="reinitialize_all_devices"
                  />
                )}
                {/** COMPONENTS FORM - */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputNumber
                    inputValue={cdoLn1.terminating_zeroes_expected.set_one_byte}
                    inputOnChange={onChangeFormValueCdoLn1}
                    inputLabelName={t("general.setOneByte")}
                    inputVariableName="set_one_byte"
                  />
                )}
                {/** COMPONENTS FORM - */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputNumber
                    inputValue={cdoLn1.terminating_zeroes_expected.rvu}
                    inputOnChange={onChangeFormValueCdoLn1}
                    inputLabelName={t("general.rvu")}
                    inputVariableName="rvu"
                  />
                )}
                {/** COMPONENTS FORM - */}
                {["ln1", "cga"].includes(adapter.protocol_type.toLowerCase()) && (
                  <FormInputNumber
                    inputValue={cdoLn1.terminating_zeroes_expected.rvu_heartbeat}
                    inputOnChange={onChangeFormValueCdoLn1}
                    inputLabelName={t("general.rvuHeartBeat")}
                    inputVariableName="rvu_heartbeat"
                  />
                )}
                {/** COMPONENTS FORM - IDL File */}
                {adapter.protocol_type.toLowerCase() === "mce" && (
                  <FormInputText
                    inputValue={cdoMce.idl_file}
                    inputLabelName={t("general.idlFile")}
                    inputOnChange={onChangeFormValueCdoMce}
                    inputVariableName="idl_file"
                    isRequired
                    disabledInput={false}
                  />
                )}
                {/** COMPONENTS FORM - PRODUCT TYPE */}
                {adapter.protocol_type.toLowerCase() === "mce" && (
                  <FormSelectProductType
                    inputValue={cdoMce.product_type}
                    inputOnChange={onChangeProductTypeValue}
                    inputLabelName={t("general.productType")}
                  />
                )}
                {/** COMPONENTS FORM - Reporting address */}
                {adapter.protocol_type.toLowerCase() === "mce" && (
                  <FormInputText
                    inputValue={cdoMce.event_reporting_address}
                    inputLabelName={t("general.reportingAddress")}
                    inputOnChange={onChangeFormValueCdoMce}
                    inputVariableName="reporting_address"
                    isRequired
                    disabledInput={false}
                  />
                )}
                {/** COMPONENTS FORM - Event Mapping File */}
                {adapter.protocol_type.toLowerCase() === "mce" && (
                  <FormInputText
                    inputValue={cdoMce.event_mapping_file}
                    inputLabelName={t("general.eventMappingFile")}
                    inputOnChange={onChangeFormValueCdoMce}
                    inputVariableName="mapping_file"
                    isRequired
                    disabledInput={false}
                  />
                )}
                {/** COMPONENTS FORM - Event Mapping Data */}
                {adapter.protocol_type.toLowerCase() === "mce" && (
                  <FormInputText
                    inputValue={cdoMce.event_mapping_data}
                    inputLabelName={t("general.eventMappingData")}
                    inputOnChange={onChangeFormValueCdoMce}
                    inputVariableName="mapping_data"
                    isRequired
                    disabledInput={false}
                  />
                )}
              </div>
            </div>
            <br />
          </>
        )}
        {/** COMPONENTS FORM - OPC */}
        {adapter.protocol_type.toLowerCase() === "opc" && (
          <CdoOpcPage setCdo={setCdoOpc} cdoValue={cdoOpc} />
        )}
        {/** COMPONENTS FORM - SEQUENT */}
        {adapter.protocol_type.toLowerCase() === "sequent" && (
          <CdoSequentPage setCdo={setCdoSequent} cdoValue={cdoSequent} />
        )}
        {/** COMPONENTS FORM - MODBUS */}
        {adapter.protocol_type.toLowerCase() === "modbus" && (
          <CdoModbusPage setCdo={setCdoModbus} cdoValue={cdoModbus} />
        )}
        {/** ************************************************* */}
        {/** COMPONENT DRAWER DIVIDER */}
        <DrawerRowDivider icon={faPlugCircleCheck} title={t("adapter.authTypeInformation")} />
        {/** ------------------------------------------------------------- */}
        {/** AUTH TYPE INFORMATION  */}
        <div className="container">
          <div className={`drawer__box__${theme} row`}>
            {/** COMPONENTS FORM - SELECT AUTH TYPE */}
            <FormSelectAuthType
              inputValue={adapter.auth_type}
              inputOnChange={onChangeFormValue}
              inputLabelName="Auth Type"
              inputVariableName="auth_type"
            />
            {/** COMPONENTS FORM - INPUT AUTH TYPE USERNAME */}
            {(formCreateEdit === "CREATE" || formCreateEdit === "DUPLICATE") && (
              <FormInputText
                inputValue={adapter.auth_credentials.username}
                inputLabelName={t("general.userName")}
                inputOnChange={onChangeFormValueAuthCredentials}
                inputVariableName="username"
                isRequired
                disabledInput={false}
              />
            )}
            {/** COMPONENTS FORM - INPUT AUTH TYPE PASSWORD */}
            {(formCreateEdit === "CREATE" || formCreateEdit === "DUPLICATE") && (
              <FormInputText
                inputValue={adapter.auth_credentials.password}
                inputLabelName={t("general.password")}
                inputOnChange={onChangeFormValueAuthCredentials}
                inputVariableName="password"
                isRequired
                disabledInput={false}
              />
            )}
          </div>
        </div>
        {/** ---------------------------------------------------- */}
        {/** GLOBAL ALERT */}
        <GlobalAlert2
          isError={isErrorPostAdapter}
          isSuccess={isSuccessPostAdapter}
          requestType="POST"
          error={errorPostAdapter}
          name="Adapter"
        />
      </Spin>
    </Drawer>
  );
}

export default AdaptersCreateEditForm;
